/**
 * ユーザー詳細(ドロワー)の「端末証明書」小表。
 *
 * ここでは**発行しない**(発行は「ユーザー > 端末証明書」の一覧から。
 * 発行するとパスワードを 1 回だけ見せる必要があり、ドロワーの中では見落としやすい)。
 * このタブでできるのは「入替リクエスト」まで。実際の失効は一覧の担当者がやる。
 */
import { Alert, Button, Form, Input, Modal, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";

import type { DeviceCert, DeviceCertOs, DeviceCertStatus } from "../../lib/api";
import { hasFeature } from "../../lib/features";
import { jst } from "../../lib/format";
import { useMe } from "../../lib/useApi";
import { useRequestReplaceDeviceCert, useUserDeviceCerts } from "./deviceCertHooks";
import {
  DEVICE_CERT_OS_LABEL,
  DEVICE_CERT_STATUS_LABEL,
  deviceCertStatusColor,
} from "./deviceCertOptions";
import { errorText, useIdpPerms } from "./hooks";

const { Text } = Typography;

/** 入替を頼める状態か(失効・期限切れは「入替」ではなく新規発行)。 */
const replaceable = (s: DeviceCertStatus): boolean => s !== "revoked" && s !== "expired";

function ReplaceModal({ cert, onClose }: { cert: DeviceCert | null; onClose: () => void }) {
  const [form] = Form.useForm<{ note?: string }>();
  const request = useRequestReplaceDeviceCert();

  useEffect(() => {
    if (cert) form.resetFields();
  }, [cert, form]);

  return (
    <Modal
      open={!!cert}
      title="端末証明書の入替を頼む"
      onCancel={onClose}
      onOk={() => void form.submit()}
      okText="リクエストを出す"
      cancelText="やめる"
      confirmLoading={request.isPending}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="ここではまだ失効しない"
        description="一覧の「入替リクエスト」に出るので、担当者が古いものを失効させて新しく発行する。端末をなくしたなど急ぐときは、一覧から直接失効させる。"
      />
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) =>
          request.mutate({ id: cert!.id, note: v.note ?? "" }, { onSuccess: onClose })
        }
      >
        <Form.Item
          name="note"
          label="理由・事情(履歴に残る)"
          rules={[{ required: true, message: "理由を書く" }]}
        >
          {/* API の上限は `DEVICE_CERT_NOTE_MAX`(200)。UI も同じにする(fix1 低 3) */}
          <Input.TextArea rows={3} maxLength={200} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export function DeviceCertTab({ userId }: { userId: string | null }) {
  const { canRun } = useIdpPerms();
  const me = useMe();
  // **viewer と、機能 `idp.device-cert` の無い契約には問い合わせない**。一覧 API は
  // operator 以上 + feature 必須なので、叩いても 403 が出るだけ。タブ自体も出さない(`UserDetailPage`)
  const certs = useUserDeviceCerts(userId, !!userId && canRun && hasFeature(me.data, "idp.device-cert"));
  const [replacing, setReplacing] = useState<DeviceCert | null>(null);

  const columns: ColumnsType<DeviceCert> = [
    {
      title: "状態",
      dataIndex: "status",
      width: 320,
      render: (v: DeviceCertStatus, row) => (
        <Space size={4} wrap>
          <Tag color={deviceCertStatusColor(v)}>{DEVICE_CERT_STATUS_LABEL[v]}</Tag>
          {row.replace_requested && <Tag color="gold">入替リクエスト中</Tag>}
        </Space>
      ),
    },
    {
      title: "OS",
      dataIndex: "os",
      width: 160,
      render: (v: DeviceCertOs) => DEVICE_CERT_OS_LABEL[v] ?? v,
    },
    {
      title: "有効期限",
      dataIndex: "not_after",
      width: 180,
      render: (v: string) => jst(v) || "—",
    },
    {
      title: "操作",
      key: "actions",
      width: 140,
      render: (_: unknown, row) =>
        canRun ? (
          <Button
            type="link"
            size="small"
            disabled={!replaceable(row.status) || row.replace_requested}
            onClick={() => setReplacing(row)}
          >
            入替リクエスト
          </Button>
        ) : null,
    },
  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {certs.isError && (
        <Alert
          type="error"
          showIcon
          message="端末証明書を取得できなかった"
          description={errorText(certs.error)}
        />
      )}
      <Table<DeviceCert>
        scroll={{ x: "max-content" }}
        rowKey="id"
        size="small"
        loading={certs.isFetching}
        columns={columns}
        dataSource={certs.data?.items ?? []}
        pagination={false}
        locale={{ emptyText: "端末証明書はまだない" }}
      />
      <Text type="secondary">
        発行と失効は「ユーザー &gt; 端末証明書」から。p12 のパスワードは発行のときに 1
        回だけ出るので、ここからは見られない。
      </Text>
      <ReplaceModal cert={replacing} onClose={() => setReplacing(null)} />
    </Space>
  );
}
